<template>
  <div class="ordering-table-section" v-if="gettingReportTemplateCompleted">
    <div>
      <h2>Generated report info</h2>
      <div v-if="createdReportId" id="report-page-link">
        <a :href="reportPageLink">Report page link</a>
      </div>
      <div v-if="createdReportSupplier" class="report-supplier">
        <p><b>Supplier</b>: {{ createdReportSupplier }}</p>
      </div>
    </div>

    <v-divider class="mb-7 mt-4 grey lighten-1"></v-divider>

    <div>
      <h2>Report table</h2>
      <div class="columns-visibility-toggle-container">
        <div>
          <v-btn
            dark
            class="open-in-window"
            color="indigo"
            elevation="3"
            id="open-report-in-window">
            <download-excel :data="excelExportOrderingData">Download Excel</download-excel>
          </v-btn>
        </div>
        &ensp;
        <div class="toggles-header">
          <h3>Show/hide columns</h3>
          <button @click="toggleOrderingTogglesVisibility">
            <i v-if="orderingTogglesVisible" class="fa fa-angle-down toggles-visibility-control"></i>
            <i v-if="!orderingTogglesVisible" class="fa fa-angle-up toggles-visibility-control"></i>
          </button>
        </div>
        <div class="columns-visibility-toggle-list" v-if="orderingTogglesVisible">
          <div v-for="column in availableColumns" :key="column.field" class="column-visibility-toggle">
            <v-checkbox
              class="m-0"
              color="teal"
              v-model="columnsVisibilityComputed"
              :value="column.field"
              :label="column.name"
              hide-details
            ></v-checkbox>
          </div>
        </div>
      </div>

      <div id="report-container" ref="reportTableContainer">
        <v-text-field
          v-if="!isInWindowPortal"
          v-model="search"
          label="Search"
          hide-details
          append-icon="fa-search"
          clearable
          outlined
          dense
          class="ordering-table-search"
        ></v-text-field>
        <v-data-table
          :headers="headersFiltered"
          :items="reportTemplate"
          item-key="ProductID"
          :sort-by="['Product Name']"
          :sort-desc="[true]"
          :class="'elevation-2 ' + (isInWindowPortal ? 'my-1' : 'my-5')"
          :custom-sort="customSort"
          :height="isInWindowPortal ? 'auto' : '1000px'"
          :items-per-page="itemsPerPage"
          :loading="gettingReportTemplate"
          loading-text="Loading... Please wait"
          :footer-props="{'items-per-page-options':[-1, 10, 20, 30, 50, 100]}"
          dense
          multi-sort
          :disable-pagination="isInWindowPortal"
          fixed-header
          caption="Ordering table"
          :search="search"
          ref="table"
        >
          <template v-slot:[`item.ActualOrder`]="props" v-if="!isInWindowPortal">
            <v-edit-dialog
              v-model:return-value="props.item.ActualOrder"
              @save="saveEditedRow(props.item, `ActualOrder`, props.index)"
              @cancel="cancelRowEdition(props.index)"
              @open="openRowEditingSnack"
              @close="closeRowEditingSnack()"
            >
              <v-chip :ref="'element' + props.index">
                {{ props.item.ActualOrder }}
              </v-chip>
              <template v-slot:input>
                <v-text-field
                  v-model="props.item.ActualOrder"
                  @focus="$event.target.select()"
                  label="Edit"
                  single-line
                  :type="'number'"
                ></v-text-field>
              </template>
            </v-edit-dialog>
          </template>
        </v-data-table>
      </div>

      <div class="report-table-bottom-controls" v-if="!isInWindowPortal">
        <div class="btns">
          <v-btn
            id="save-changes-btn" 
            color="success"
            class="px-10 mr-6 bold"
            large
            @click="save()"
          >
          <span v-if="savingReport">Saving..</span>
          <span v-if="!savingReport">Save</span>
          </v-btn>
          <!-- <v-btn
            width="47"
            height="47"
            fab
            dark
            large
            color="indigo"
            id="open-report-in-window" 
            @click="triggerOrderingTableWindowOpen">
            <i class="fas fa-external-link-alt open-tbl-btn"></i>
          </v-btn> -->
        </div>
        <v-divider vertical class="mx-9 grey" v-show="savingReportCompleted || savingReportError"></v-divider>
        <div class="msgs">
          <span v-if="savingReportCompleted">Saved changes</span>
          <span v-if="savingReportError" id="save-changes-error" class="error-msg">
            Error saving changes
          </span>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex';
import { sortItems } from 'vuetify/es5/util/helpers';

import session from '../api/session';
import { MonthPicker } from 'vue-month-picker';
import WindowPortal from '../components/WindowPortal';

export default {
  props: {
    isInWindowPortal: Boolean,
  },
  data() {
    return {
      itemsPerPage: 50,
      space: ' ',
      snack: false,
      snackColor: '',
      snackText: '',
      columnsVisibility: [],
      changes: [],
      orderingTogglesVisible: false,
      search: '',
    };
  },
  computed: {
    ...mapState('ordering', [
      'reportTemplate',
      'gettingReportTemplate',
      'gettingReportTemplateCompleted',
      'gettingReportTemplateError',
      'savingReport',
      'savingReportCompleted',
      'savingReportError',
      'createdReportId',
      'createdReportSupplier',
      'createdReportMonth',
      'createdReportOrderPeriod',
      'orderingReportTableWindowOpen',
      'excelExportOrderingData'
    ]),
    reportPageLink() {return `/ordering_report/${this.createdReportId}?supplier=${this.createdReportSupplier}` + `&month=${ this.createdReportMonth }&period=${ this.createdReportOrderPeriod}`},
    availableColumns() {
      let columns = new Array();
      for (const hdr of this.headers) {
        const column = {
          name: hdr.text,
          field: hdr.value
        }
        columns.push(column);
      }
      return columns;
    },
    columnsVisibilityComputed: {
      get() {
        if (this.columnsVisibility.length == 0) {
          let visibility = [];
          for (const column of this.availableColumns) {
            visibility.push(column.field);
          }
          return visibility;
        }
        return this.columnsVisibility;
      },
      set(value) {
        this.columnsVisibility = value;
      },
    },
    headers() {
      return [
        { value: 'ProductID', text: 'Product ID', filterable: true, groupable: false, },
        { value: 'Product Name', text: 'Name', filterable: true, groupable: false },
        { value: 'Product Description', text: 'Description', filterable: true, groupable: false, },
        { value: 'Barcode', text: 'Barcode', filterable: true, groupable: false, },
        { value: 'LastSoldDate', text: 'Last sold date', filterable: false, groupable: false, },
        //{ value: 'AvgOrderPeriodSales', text: 'Average sales for order period', filterable: false, groupable: false, },
        { value: 'Last3MonthsSales', text: 'Last 3 months sales', filterable: false, groupable: false, },
        { value: 'LastOrderPeriodSales', text: 'Last order period sales', filterable: false, groupable: false, },
        { value: 'OrderPeriodLastYearSales', text: 'This order period last year sales', filterable: false, groupable: false, },
        { value: 'ActualOrder', text: 'Actual order', filterable: false, groupable: false, },
      ]
    },
    headersFiltered() {
      let hdrs = [];
      this.headers.forEach((hdr) => {
        if (this.columnsVisibilityComputed.includes(hdr.value)) hdrs.push(hdr);
      })
      return hdrs;
    },
    showTableOnly() {
      return (this.orderingTableWindowOpen && this.isInWindowPortal);
    }
  },
  methods: {
    save() {
      this.$store.dispatch('ordering/saveOrderingReport', {reportEntityId: this.createdReportId, changes: this.changes});
      this.changes.splice(0);
    },
    saveEditedRow(row, field, index) {
      this.snack = true;
      if (row.ActualOrder === ''){
         this.clickOnNextEdit(index); 
         return;
      }
      this.changes.push({value: row.ActualOrder, productId: row.ProductID});
      this.snackColor = 'success'
      this.snackText = 'Data saved'
      this.clickOnNextEdit(index);
    },
    cancelRowEdition(index) {
      this.snack = true
      this.snackColor = 'error'
      this.snackText = 'Canceled'
      this.clickOnNextEdit(index);
    },
    openRowEditingSnack() {
      this.snack = true
      this.snackColor = 'info'
      this.snackText = 'Dialog opened'
    },
    closeRowEditingSnack() {
      this.snack = false;
    },
    triggerOrderingTableWindowOpen() {
      if (this.orderingReportTableWindowOpen) {
        this.$store.dispatch('ordering/closeOrderingTableWindow');
        setTimeout(() => this.$store.dispatch('ordering/openOrderingTableInWindow'), 1000)
      } else {
        this.$store.dispatch('ordering/openOrderingTableInWindow');
      }
    },
    toggleOrderingTogglesVisibility() {
      this.orderingTogglesVisible = !this.orderingTogglesVisible;
    },
    naturalCompare(a, b) {
      var ax = [], bx = [];

      a.replace(/(\d+)|(\D+)/g, function(_, $1, $2) { ax.push([$1 || Infinity, $2 || '']) });
      b.replace(/(\d+)|(\D+)/g, function(_, $1, $2) { bx.push([$1 || Infinity, $2 || '']) });
      
      while(ax.length && bx.length) {
          var an = ax.shift();
          var bn = bx.shift();
          var nn = (an[0] - bn[0]) || an[1].localeCompare(bn[1]);
          if(nn) return nn;
      }

      return ax.length - bx.length;
    },
    sortingFunc(el1, el2) {
      let a = String(el1).trim().toLowerCase().split(' ').join('0');
      let b = String(el2).trim().toLowerCase().split(' ').join('0');
      return this.naturalCompare(b, a);
    },
    customSort(items, sortBy, sortDesc, locale, customSorters) {
      const sorters = {
        'Product Name': this.sortingFunc,
        'Category Name': this.sortingFunc,
        'Supplier': this.sortingFunc,
      }
      return sortItems(items, sortBy, sortDesc, locale, sorters);
    },
    clickOnNextEdit(index) {
      this.$nextTick(() => {
        const editButtonRef = this.$refs['element' + (index + 1)];
        if(editButtonRef != undefined){
            editButtonRef.$el.click();
        }
      });
    }
  }
};

</script>
 

<style>

#report-page-link {
  margin-bottom: 10px;
}

.report-table-bottom-controls .btns {
  display: flex;
  align-items: center;
  margin-bottom: 0;
}

.report-table-bottom-controls .msgs {
  font-weight: 500;
  margin-left: 0;
}

.report-table-bottom-controls {
  display: flex;
  align-items: center;
  margin-top: 25px;
}

.ordering-table-search {
  max-width: 500px;
}

</style>



// WEBPACK FOOTER //
// src/components/OrderingTable.vue